// client/src/components/fidelidade/FidelidadePage.js
import React, { Fragment, useState, useCallback } from 'react';
import { Gift, PlusCircle, XCircle, Loader2, MoreVertical, Edit, Trash2, Award, PackageCheck, PackageX, Clock, AlertCircle } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';
import PremioForm from './PremioForm';
import {
    usePremiosFidelidade,
    useMutatePremiosFidelidade,
    useResgatesFidelidade,
    useMutateResgatesFidelidade,
} from '../../hooks/useFidelidade';

const STATUS_RESGATE = {
    pendente: { label: 'Pendente', bg: '#fef3c7', color: '#92400e', Icon: Clock },
    entregue: { label: 'Entregue', bg: '#dcfce7', color: '#166534', Icon: PackageCheck },
    cancelado: { label: 'Cancelado', bg: '#fee2e2', color: '#991b1b', Icon: PackageX },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_RESGATE[status] || STATUS_RESGATE.pendente;
    const { Icon } = cfg;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 99, fontSize: '0.8rem', fontWeight: 600, background: cfg.bg, color: cfg.color }}>
            <Icon size={12} /> {cfg.label}
        </span>
    );
};

const nomeCliente = (cliente) => (typeof cliente === 'object' ? cliente?.nome || cliente?.user?.name : null) || 'Cliente';

const errMsg = (err, fallback) => err?.response?.data?.error || err?.response?.data?.message || fallback;

const ErroCarregamento = ({ mensagem }) => (
    <div className="table-empty-state">
        <AlertCircle size={48} style={{ color: '#dc2626' }} />
        <h3>Não foi possível carregar</h3>
        <p>{mensagem}</p>
    </div>
);

// ---------------------------------------------------------------------
// Aba: Catálogo de prêmios
// ---------------------------------------------------------------------
const AbaPremios = () => {
    const [showForm, setShowForm] = useState(false);
    const [editando, setEditando] = useState(null);
    const [selecionado, setSelecionado] = useState(null);

    const { data: premios = [], isLoading, isError, error } = usePremiosFidelidade();
    const { criarPremio, atualizarPremio, excluirPremio } = useMutatePremiosFidelidade();
    const isMutating = criarPremio.isPending || atualizarPremio.isPending || excluirPremio.isPending;

    const handleAddNew = useCallback(() => { setEditando(null); setShowForm(true); }, []);
    const handleEdit = useCallback((premio) => { setEditando(premio); setShowForm(true); setSelecionado(null); }, []);
    const handleCloseForm = useCallback(() => { setShowForm(false); setEditando(null); }, []);

    const handleSalvar = useCallback((payload) => {
        if (editando) {
            atualizarPremio.mutate({ id: editando._id, ...payload }, { onSuccess: handleCloseForm });
        } else {
            criarPremio.mutate(payload, { onSuccess: handleCloseForm });
        }
    }, [editando, atualizarPremio, criarPremio, handleCloseForm]);

    const handleExcluir = useCallback((premio) => {
        if (window.confirm(`Tem certeza que deseja remover o prêmio "${premio.nome}"?`)) {
            excluirPremio.mutate(premio._id, { onSuccess: () => setSelecionado(null) });
        }
    }, [excluirPremio]);

    return (
        <Fragment>
            <div className="page-management-container" style={{ paddingTop: 0 }}>
                {showForm && (
                    <PremioForm premio={editando} onSalvar={handleSalvar} onCancelar={handleCloseForm} isPending={isMutating} />
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state"><Loader2 size={24} className="animate-spin" /><span>Carregando prêmios...</span></div>
                        ) : isError ? (
                            <ErroCarregamento mensagem={errMsg(error, 'Erro ao carregar o catálogo de prêmios.')} />
                        ) : premios.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Prêmio</th>
                                        <th>Custo em Pontos</th>
                                        <th>Estoque</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {premios.map((premio) => (
                                        <tr key={premio._id} style={{ opacity: premio.ativo ? 1 : 0.6 }}>
                                            <td>
                                                <strong>{premio.nome}</strong>
                                                {premio.descricao && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{premio.descricao}</div>}
                                            </td>
                                            <td>{premio.custoPontos} pts</td>
                                            <td>{premio.quantidadeDisponivel == null ? <span className="td-empty">Ilimitado</span> : premio.quantidadeDisponivel}</td>
                                            <td>
                                                <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: premio.ativo ? '#dcfce7' : '#f1f5f9', color: premio.ativo ? '#166534' : '#64748b' }}>
                                                    {premio.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <button onClick={() => setSelecionado(premio)} className="action-button-icon text-gray-600" title="Ações">
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Award size={48} />
                                <h3>Nenhum prêmio cadastrado</h3>
                                <p>Crie prêmios que seus clientes podem trocar por pontos.</p>
                                <button onClick={handleAddNew} className="action-button action-button-primary" style={{ marginTop: '1rem' }}>
                                    <PlusCircle size={18} className="icon" /> Novo Prêmio
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton onClick={handleAddNew} title="Novo Prêmio" visible={!showForm} icon={<Gift size={26} className="icon" />} />

            <ActionsDrawer
                open={!!selecionado}
                onClose={() => setSelecionado(null)}
                title={selecionado?.nome || 'Prêmio'}
                actions={selecionado ? [
                    { key: 'editar', label: 'Editar', icon: <Edit size={20} className="icon" />, onClick: () => handleEdit(selecionado) },
                    { key: 'excluir', label: 'Remover', icon: <Trash2 size={20} className="icon" />, onClick: () => handleExcluir(selecionado), destructive: true, loading: excluirPremio.isPending },
                ] : []}
            />
        </Fragment>
    );
};

// ---------------------------------------------------------------------
// Aba: Fila de resgates
// ---------------------------------------------------------------------
const AbaResgates = () => {
    const [filtroStatus, setFiltroStatus] = useState('pendente');
    const { data, isLoading, isError, error } = useResgatesFidelidade({ status: filtroStatus || undefined, limit: 50 });
    const { confirmarEntrega, cancelarResgate } = useMutateResgatesFidelidade();
    const resgates = data?.data || [];

    const handleCancelar = useCallback((resgate) => {
        const motivo = window.prompt(`Cancelar o resgate de "${resgate.nomePremioSnapshot}" para ${nomeCliente(resgate.cliente)}? Os pontos serão estornados.\n\nMotivo (opcional):`);
        if (motivo === null) return; // usuário cancelou o prompt
        cancelarResgate.mutate({ id: resgate._id, motivo: motivo || undefined });
    }, [cancelarResgate]);

    return (
        <Fragment>
            <div className="page-management-container" style={{ paddingTop: 0 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                    {['pendente', 'entregue', 'cancelado', ''].map((s) => (
                        <button
                            key={s || 'todos'}
                            onClick={() => setFiltroStatus(s)}
                            className={`action-button ${filtroStatus === s ? 'action-button-primary' : 'action-button-secondary'}`}
                            style={{ fontSize: '0.8125rem' }}
                        >
                            {s ? STATUS_RESGATE[s].label : 'Todos'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state"><Loader2 size={24} className="animate-spin" /><span>Carregando resgates...</span></div>
                    ) : isError ? (
                        <ErroCarregamento mensagem={errMsg(error, 'Erro ao carregar a fila de resgates.')} />
                    ) : resgates.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Prêmio</th>
                                    <th>Pontos</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resgates.map((resgate) => (
                                    <tr key={resgate._id}>
                                        <td>{nomeCliente(resgate.cliente)}</td>
                                        <td>{resgate.nomePremioSnapshot}</td>
                                        <td>{resgate.custoPontosSnapshot} pts</td>
                                        <td><StatusBadge status={resgate.status} /></td>
                                        <td>{new Date(resgate.createdAt).toLocaleDateString('pt-BR')}</td>
                                        <td className="text-right">
                                            {resgate.status === 'pendente' && (
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={() => confirmarEntrega.mutate(resgate._id)}
                                                        className="action-button action-button-primary"
                                                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                                                        disabled={confirmarEntrega.isPending}
                                                    >
                                                        Confirmar Entrega
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancelar(resgate)}
                                                        className="action-button action-button-secondary"
                                                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                                                        disabled={cancelarResgate.isPending}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <PackageCheck size={48} />
                            <h3>Nenhum resgate encontrado</h3>
                            <p>{filtroStatus === 'pendente' ? 'Não há resgates de prêmio aguardando entrega.' : 'Nenhum resgate nesse status.'}</p>
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

// ---------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------
const FidelidadePage = () => {
    const [aba, setAba] = useState('premios');

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Gift />}
                    title="Fidelidade"
                    subtitle="Catálogo de prêmios e fila de resgates"
                />
                <div className="form-tabs-nav">
                    <button type="button" className={`tab-button ${aba === 'premios' ? 'is-active' : ''}`} onClick={() => setAba('premios')}>
                        <Award size={16} /> Catálogo de Prêmios
                    </button>
                    <button type="button" className={`tab-button ${aba === 'resgates' ? 'is-active' : ''}`} onClick={() => setAba('resgates')}>
                        <PackageCheck size={16} /> Fila de Resgates
                    </button>
                </div>
            </div>

            {aba === 'premios' ? <AbaPremios /> : <AbaResgates />}
        </Fragment>
    );
};

export default FidelidadePage;
